// naim-web/src/components/NewsModal.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Bell, X, Star, Wrench, Gift, Info } from 'lucide-react';

const TYPE_CONFIG = {
    feature: { Icon: Star, color: '#4CAF50', bgColor: 'rgba(76, 175, 80, 0.15)' },
    fix: { Icon: Wrench, color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.15)' },
    promo: { Icon: Gift, color: '#EAB308', bgColor: 'rgba(234, 179, 8, 0.15)' },
    info: { Icon: Info, color: '#6B7280', bgColor: 'rgba(107, 114, 128, 0.15)' },
};

function formatDate(ts) {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function NewsModal({ visible, news, onClose }) {
    const { t } = useTranslation();
    const { themeMode } = useTheme();
    const isForest = themeMode === 'forest'; // Derive from themeMode
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Forest theme colors for naim
    const colors = {
        bg: isForest ? '#1a4d40' : '#ffffff',
        border: isForest ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
        bellIcon: isForest ? '#69f0ae' : '#4CAF50',
        headerText: isForest ? '#e8f5e9' : '#1B5E20',
        closeIcon: isForest ? '#a5d6a7' : '#6b7280',
        sidebarSelected: isForest ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
        dateText: isForest ? '#a5d6a7' : '#6b7280',
        titleText: isForest ? '#e8f5e9' : '#374151',
        bodyText: isForest ? '#c8e6c9' : '#4b5563',
        button: '#4CAF50',
    };

    // Reset selected index when modal opens
    useEffect(() => {
        if (visible) {
            setSelectedIndex(0);
        }
    }, [visible]);

    if (!visible || !news?.length) return null;

    const selectedNews = news[selectedIndex] || news[0];
    const config = TYPE_CONFIG[selectedNews?.type] || TYPE_CONFIG.info;
    const TypeIcon = config.Icon;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
            <div
                className="w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl"
                style={{
                    backgroundColor: colors.bg,
                    maxHeight: '75vh',
                }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-4 py-3"
                    style={{ borderBottom: `1px solid ${colors.border}` }}
                >
                    <div className="flex items-center gap-2">
                        <Bell size={20} color={colors.bellIcon} />
                        <span className="text-lg font-bold" style={{ color: colors.headerText }}>
                            {t('news.title', "What's New")}
                        </span>
                    </div>
                    <button onClick={onClose} className="p-1 hover:opacity-70 transition">
                        <X size={22} color={colors.closeIcon} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex" style={{ minHeight: 200, maxHeight: 400 }}>
                    {/* Left sidebar - news list */}
                    {news.length > 1 && (
                        <div
                            className="w-32 py-2 overflow-y-auto"
                            style={{ borderRight: `1px solid ${colors.border}` }}
                        >
                            {news.map((item, idx) => {
                                const itemConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG.info;
                                const ItemIcon = itemConfig.Icon;
                                const isSelected = idx === selectedIndex;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => setSelectedIndex(idx)}
                                        className="w-full text-left px-3 py-2 transition"
                                        style={{
                                            backgroundColor: isSelected ? colors.sidebarSelected : 'transparent',
                                        }}
                                    >
                                        <div className="text-[10px]" style={{ color: colors.dateText }}>
                                            {formatDate(item.createdAt)}
                                        </div>
                                        <div
                                            className="w-5 h-5 rounded flex items-center justify-center my-1"
                                            style={{ backgroundColor: itemConfig.bgColor }}
                                        >
                                            <ItemIcon size={12} color={itemConfig.color} />
                                        </div>
                                        <div
                                            className="text-xs truncate"
                                            style={{
                                                color: colors.titleText,
                                                fontWeight: isSelected ? 600 : 400,
                                            }}
                                        >
                                            {item.title}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Main content */}
                    <div className="flex-1 p-5 overflow-y-auto">
                        <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                            style={{ backgroundColor: config.bgColor }}
                        >
                            <TypeIcon size={24} color={config.color} />
                        </div>
                        <h3 className="text-xl font-bold mb-1" style={{ color: colors.headerText }}>
                            {selectedNews.title}
                        </h3>
                        <div className="text-xs mb-4" style={{ color: colors.dateText }}>
                            {formatDate(selectedNews.createdAt)}
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: colors.bodyText, whiteSpace: 'pre-wrap' }}>
                            {selectedNews.body}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div
                    className="px-4 py-3 flex justify-center"
                    style={{ borderTop: `1px solid ${colors.border}` }}
                >
                    <button
                        onClick={onClose}
                        className="px-8 py-2.5 rounded-lg text-white font-semibold transition hover:opacity-90"
                        style={{ backgroundColor: colors.button }}
                    >
                        {t('news.gotIt', 'Got it!')}
                    </button>
                </div>
            </div>
        </div>
    );
}

